import { useMemo } from "react";
import type { z } from "zod";

import type { Prettify } from "@thalamos/common";
import type { WorkflowVersion } from "@thalamos/vision-common";
import {
  createInitialValues,
  FormSectionSchema,
  tk,
  workflows,
} from "@thalamos/vision-common";

import type { MultiPageFormProps } from "../../components/FormBuilder/MultiPageForm/MultiPageForm.js";
import {
  MultiPageForm,
  MultiPageFormSkeleton,
} from "../../components/FormBuilder/MultiPageForm/MultiPageForm.js";
import { Error } from "../../pages/Error/Error.js";

type McaIncidentDetailsSchema<T extends WorkflowVersion> = z.infer<
  ReturnType<
    (typeof workflows)[T]["mentalCapacityAct"]["formDefinition"]["sections"][1]["buildFormSpecification"]
  >["formSchema"]
>;

///

export function McaIncidentDetailsFormSkeleton() {
  return <MultiPageFormSkeleton headerText={tk("mcaIncidentDetails.title")} />;
}

///

export type McaIncidentDetailsFormProps<T extends WorkflowVersion> = Prettify<
  {
    workflowVersion: T;
  } & Pick<
    MultiPageFormProps<McaIncidentDetailsSchema<T>>,
    | "initialValues"
    | "incidentWorkItem"
    | "onAutoSave"
    | "onSubmit"
    | "onCancel"
  >
>;

///

export function McaIncidentDetailsForm<T extends WorkflowVersion>(
  props: McaIncidentDetailsFormProps<T>,
) {
  const workflow = workflows[props.workflowVersion];
  const section = workflow?.["mentalCapacityAct"].formDefinition.sections.find(
    (section) => section.id === FormSectionSchema.enum.mcaIncidentDetails,
  );
  const formSpecification = section?.buildFormSpecification({
    incidentWorkItem: props.incidentWorkItem,
  });
  const formSchema = formSpecification?.formSchema;
  const strictFieldSchemas = formSpecification?.strictFieldSchemas;

  const initialValuesOrDefaults: Record<string, unknown> = useMemo(() => {
    if (!section || !strictFieldSchemas || !formSchema) {
      return {};
    } else {
      return createInitialValues(strictFieldSchemas, props.initialValues);
    }
  }, [props, strictFieldSchemas, section, formSchema]);

  if (!section || !formSchema || !strictFieldSchemas) {
    return <Error />;
  }

  return (
    <MultiPageForm<typeof formSchema>
      headerText={tk("mcaIncidentDetails.title")}
      incidentWorkItem={props.incidentWorkItem}
      schema={formSchema}
      strictFieldSchemas={strictFieldSchemas}
      initialValues={initialValuesOrDefaults}
      pages={formSpecification.pages}
      onCancel={props.onCancel}
      onSubmit={props.onSubmit}
      onAutoSave={props.onAutoSave}
    />
  );
}
